import { Alert, Form, Input } from "antd";
import { useState } from "react";
import { errorText } from "../lib/api";
import { signIn, signUp } from "../lib/session";

const valueProps = [
  "Checks run on runners inside your own network",
  "Kibana, Dynatrace and Rancher alerts in one inbox",
  "One incident per problem in PagerDuty and Jira",
];

export default function LoginPage() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const submit = async (values: { name?: string; orgName?: string; email: string; password: string }) => {
    setBusy(true);
    setError(null);
    try {
      if (isLogin) await signIn(values.email, values.password);
      else await signUp({ name: values.name!, orgName: values.orgName?.trim() || undefined, email: values.email, password: values.password });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-canvas">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-navy p-12 text-white lg:flex lg:w-[44%] xl:w-[40%]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px circle at 15% -10%, rgba(192,68,62,0.18), transparent 45%)" }}
        />
        <div className="relative flex items-center gap-2 font-grotesk text-xl font-bold tracking-tight">
          <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden>
            <path d="M3 17h7l3-8 5 15 3-7h8" fill="none" stroke="#C0443E" strokeWidth="3" strokeLinecap="square" />
          </svg>
          Pulse
        </div>

        <div className="relative max-w-md">
          <span className="mb-6 block h-0.5 w-10 bg-brick" />
          <h2 className="m-0 font-display text-[42px] font-semibold leading-[1.08]">Know first. Page once.</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-navy-200">
            Monitoring and alerting for infrastructure teams. You configure it here; every check runs on a runner you host.
          </p>
          <ul className="mt-8 space-y-3 p-0" style={{ listStyle: "none" }}>
            {valueProps.map((v) => (
              <li key={v} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brick" />
                <span className="text-[14px] text-navy-200">{v}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative font-grotesk text-xs tracking-wide text-navy-200">© {new Date().getFullYear()} 8lc · Pulse</div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="anim-fade w-full max-w-[400px]">
          <div className="mb-8 text-center lg:hidden">
            <div className="font-grotesk text-2xl font-bold tracking-tight text-ink">Pulse</div>
            <p className="mt-0.5 font-display text-[16px] italic text-muted">Know first. Page once.</p>
          </div>

          <div className="mb-7">
            <h1 className="m-0 font-grotesk text-[24px] font-semibold leading-tight text-ink">{isLogin ? "Welcome back" : "Create your account"}</h1>
            <p className="mt-1 text-sm text-muted">{isLogin ? "Sign in to continue" : "Name an organization to own it on a 14-day trial"}</p>
          </div>

          {error && <Alert type="error" showIcon message={error} className="mb-5" />}

          <Form key={mode} layout="vertical" requiredMark={false} onFinish={submit} disabled={busy}>
            {!isLogin && (
              <>
                <Form.Item name="name" label="Your name" rules={[{ required: true, message: "Enter your name" }]}>
                  <Input autoComplete="name" />
                </Form.Item>
                <Form.Item name="orgName" label="Organization" extra="Leave empty if your team already added you: you'll join them when you sign up with that email.">
                  <Input placeholder="Acme Payments" />
                </Form.Item>
              </>
            )}
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Enter a valid email" }]}>
              <Input autoComplete="email" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Enter your password" }, ...(isLogin ? [] : [{ min: 10, message: "Use at least 10 characters" }])]}
            >
              <Input.Password autoComplete={isLogin ? "current-password" : "new-password"} />
            </Form.Item>
            <Form.Item className="mb-0 mt-2">
              <button type="submit" className="btn-sharp btn-brick w-full" style={{ height: 42 }} disabled={busy}>
                {busy ? "One moment…" : isLogin ? "Sign in" : "Create organization"}
              </button>
            </Form.Item>
          </Form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted">{isLogin ? "New to Pulse?" : "Already have an account?"}</span>
            <span
              onClick={() => {
                setMode(isLogin ? "signup" : "login");
                setError(null);
              }}
              className="ml-1 inline cursor-pointer font-grotesk font-semibold text-navy hover:underline"
            >
              {isLogin ? "Create an organization" : "Sign in"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
